import { useId } from 'react'

import { cn } from '@/lib/cn'

export interface ToggleProps {
  checked: boolean
  onChange: (checked: boolean) => void
  label: string
  description?: string
  id?: string
  disabled?: boolean
}

/**
 * A switch with its label.
 *
 * The label is wired with `aria-labelledby` rather than a wrapping `<label
 * for>`: `for` may only point at a form control, and this switch is a
 * `<button role="switch">`, so the old markup left the switch unnamed and the
 * label inert to clicks. The wrapper handles the click instead.
 */
export function Toggle({
  checked,
  onChange,
  label,
  description,
  id,
  disabled,
}: ToggleProps) {
  const generated = useId()
  const switchId = id ?? generated
  const labelId = `${switchId}-label`
  const descriptionId = `${switchId}-description`

  return (
    <div className={cn('flex items-start gap-2.5', disabled && 'opacity-60')}>
      <button
        id={switchId}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={labelId}
        aria-describedby={description ? descriptionId : undefined}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full border',
          'transition-colors duration-150 ease-out disabled:cursor-not-allowed',
          checked
            ? 'border-accent bg-accent'
            : 'border-line bg-surface-sunken hover:border-line-strong',
        )}
      >
        <span
          className={cn(
            'h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform duration-150 ease-out',
            checked ? 'translate-x-4.5' : 'translate-x-0.5',
          )}
        />
      </button>
      <span
        className={cn('min-w-0', !disabled && 'cursor-pointer select-none')}
        onClick={() => !disabled && onChange(!checked)}
      >
        <span id={labelId} className="block text-base text-ink">
          {label}
        </span>
        {description ? (
          <span id={descriptionId} className="block text-xs text-ink-muted">
            {description}
          </span>
        ) : null}
      </span>
    </div>
  )
}
